/**
 * Предупреждение о будущих записях выключенного специалиста.
 *
 * Отдельная функция ради одного `if` — потому что цена ошибки в этом `if` несоразмерна
 * его размеру. `setStaffActive` возвращает `affectedAppointments`: сколько будущих записей
 * осталось у мастера, которого владелец только что выключил. Сервис НАМЕРЕННО не отменяет
 * их сам — отмена шлёт клиенту уведомление и делается по каждой осознанно (ТЗ §11.4), —
 * и другого способа узнать о них у владельца нет. Проглоти экран это число, и клиенты
 * пришли бы к пустому креслу: та самая неявка, от которой продукт обещает избавить,
 * только со стороны бизнеса.
 */

import type { StaffActiveResultDto } from '../../../api/cabinetTypes';

/**
 * Сколько записей показать в предупреждении. `null` — предупреждать не о чем.
 *
 * `null` в двух случаях, и оба не являются умолчанием вместо данных:
 *   — специалист включён: у работающего мастера будущие записи — норма, а не риск;
 *   — записей нет: фраза «остаётся будущих записей: 0» — шум, который приучает
 *     не читать предупреждение вовсе, и в следующий раз владелец пролистает «3».
 */
export function staffOffWarningCount(result: StaffActiveResultDto): number | null {
  if (result.staff.active) return null;
  return result.affectedAppointments > 0 ? result.affectedAppointments : null;
}

/**
 * Что показать в шторке после переключения специалиста. `null` — показывать нечего.
 *
 * Поводов два, и они НЕЗАВИСИМЫ, поэтому собираются в один объект, а не в один `if`:
 *   — у выключенного мастера остались будущие записи (см. `staffOffWarningCount`);
 *   — он был последним активным, и опубликованная организация осталась в каталоге
 *     карточкой, в которую нельзя записаться.
 *
 * Свяжи их `else`-ом — и самый частый случай промолчит: единственного мастера выключают
 * обычно в тихий день, когда записей у него как раз ноль.
 *
 * Второй повод не зависит от направления переключателя: включив мастера при нуле активных
 * УСЛУГ, владелец тоже должен узнать, что записаться по-прежнему не на что. Флаг описывает
 * состояние витрины, а не действие, поэтому `staff.active` здесь не проверяется.
 */
export interface StaffOffWarnings {
  /** Число будущих записей, или `null`, если предупреждать не о чем. */
  readonly appointments: number | null;
  /** Организация в каталоге, а записаться не на что. */
  readonly unbookableInCatalog: boolean;
}

export function staffOffWarnings(result: StaffActiveResultDto): StaffOffWarnings | null {
  const appointments = staffOffWarningCount(result);
  if (appointments === null && !result.unbookableInCatalog) return null;

  return { appointments, unbookableInCatalog: result.unbookableInCatalog };
}
